import React, { useEffect, useRef, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { Platform } from 'react-native';
import { CompetitorCircuitStepResponse, GeolocationRequirement, MyMissionClient } from '../api/api.g';
import { isCircuitSurveyCorrection, requiresCircuitVisitConfirmation } from '../helpers/competitorCircuitSurvey';
import { withTimeout } from '../helpers/withTimeout';
import { lightBoxActions } from '../store/lightBoxSlice';
import Prevalidation from '../components/validation/pages/Prevalidation';
import useApi from './api-hook';
import { useAppDispatch } from './store-hook';

const useCompetitorCircuitQuestionnaire = (circuitId: string, mainMissionAssignationId?: string) => {
  const api = useApi(MyMissionClient);
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const [openingId, setOpeningId] = useState<string>();
  const [errorKey, setErrorKey] = useState<string>();
  const busy = useRef(false);
  const active = useRef(true);
  const ownsPopup = useRef(false);

  const closePopup = () => {
    ownsPopup.current = false;
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setContent(<></>));
  };

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      if (ownsPopup.current) closePopup();
    };
  }, []);

  const openQuestionnaire = async (step: CompetitorCircuitStepResponse) => {
    if (busy.current || !mainMissionAssignationId) return;
    busy.current = true;
    setOpeningId(step.missionAssignationId);
    setErrorKey(undefined);
    try {
      const response = await withTimeout(api.getMissionDetails(step.missionAssignationId, api.tokenSource?.token), 20000);
      if (!active.current) return;
      const detail = response.missionDetails;
      if (!detail || detail.competitorCircuitId !== circuitId || detail.canAnswerSurvey !== true) {
        setErrorKey(detail?.geolocationRequirement === GeolocationRequirement.Required && !detail.hasGeolocationCheckIn
          ? Platform.OS === 'web' ? 'geolocation-check-in-native-app-required' : 'geolocation-check-in-required-before-survey'
          : 'competitor-circuit-questionnaire-unavailable');
        return;
      }
      const open = () => {
        if (!active.current) return;
        navigation.navigate({
          name: isCircuitSurveyCorrection(detail.status) ? 'editSurvey' : 'survey',
          params: { missionAssignationId: step.missionAssignationId },
        } as never);
      };
      if (!requiresCircuitVisitConfirmation(step.role, detail.status, detail.inProgress)) {
        open();
        return;
      }
      ownsPopup.current = true;
      let confirmed = false;
      dispatch(lightBoxActions.setContent(<Prevalidation
        selectedMissionId={step.missionAssignationId}
        isCircuitVisit
        refreshMap
        onClosePress={closePopup}
        onConfirmPress={() => {
          if (confirmed) return;
          confirmed = true;
          closePopup();
          open();
        }}
      />));
      dispatch(lightBoxActions.setVisibility(true));
    } catch {
      if (active.current) setErrorKey('competitor-circuit-questionnaire-load-failed');
    } finally {
      busy.current = false;
      if (active.current) setOpeningId(undefined);
    }
  };

  return { openQuestionnaire, openingId, errorKey };
};

export default useCompetitorCircuitQuestionnaire;
